// frontend/src/api/admin.js
// SignalIQ Admin Control Center API client
//
// Production-safe admin API client.
// Fixes:
// - Uses the shared SignalIQ API client base/token when available.
// - Prevents accidental calls to the frontend domain.
// - Normalizes AdminEnvelope responses from backend/admin.py.
// - Adds activity/page-view logging helpers.
// - Adds compatibility aliases for /admin/audit and /admin/audit-logs.
// - Adds ingestion endpoint aliases so Admin ingestion stays inside /admin/ingestion.

import api, { getApiBaseUrl, getToken } from "./client";

const FALLBACK_API_BASE = "/api/v1";

function cleanBase(value) {
  return String(value || "").trim().replace(/\/+$/, "");
}

function resolveApiBase() {
  try {
    const sharedBase = cleanBase(getApiBaseUrl?.());
    if (sharedBase) return sharedBase;
  } catch {
    // Fall through to env/local fallback.
  }

  const raw = cleanBase(import.meta.env.VITE_API_BASE_URL || FALLBACK_API_BASE);
  return raw.endsWith("/api/v1") ? raw : `${raw}/api/v1`;
}

const API_BASE = resolveApiBase();

function getStoredToken() {
  try {
    return (
      getToken?.() ||
      localStorage.getItem("signaliq_token") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("signaliq_token") ||
      sessionStorage.getItem("access_token") ||
      sessionStorage.getItem("token") ||
      ""
    );
  } catch {
    return "";
  }
}

function buildUrl(path) {
  const safePath = String(path || "").startsWith("/")
    ? String(path || "")
    : `/${path}`;

  if (/^https?:\/\//i.test(safePath)) {
    return safePath;
  }

  // Prevent double /api/v1 when a caller accidentally passes it.
  const normalizedPath = safePath.replace(/^\/api\/v1/i, "");

  return `${API_BASE}${normalizedPath}`;
}

function buildQuery(params = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });

  const qs = query.toString();
  return qs ? `?${qs}` : "";
}

function extractErrorMessage(payload, fallback) {
  if (!payload) return fallback;

  if (typeof payload === "string") return payload;

  if (typeof payload.detail === "string") return payload.detail;
  if (typeof payload.message === "string") return payload.message;
  if (typeof payload.error === "string") return payload.error;

  if (payload.detail && typeof payload.detail === "object") {
    return (
      payload.detail.message ||
      payload.detail.error ||
      JSON.stringify(payload.detail)
    );
  }

  try {
    return JSON.stringify(payload);
  } catch {
    return fallback;
  }
}

async function request(path, options = {}) {
  const token = getStoredToken();
  const method = String(options.method || "GET").toUpperCase();

  const headers = {
    Accept: "application/json",
    ...(options.body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(buildUrl(path), {
    method,
    credentials: options.credentials || "include",
    headers,
    body: options.body,
    signal: options.signal,
  });

  const text = await res.text();

  let payload = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = { ok: false, detail: text };
  }

  if (!res.ok) {
    const fallback = `Admin request failed: ${res.status}`;

    if (res.status === 401) {
      throw new Error("Your admin session expired. Please log in again.");
    }

    if (res.status === 403) {
      throw new Error("Admin access required. Please log in with an owner/admin account.");
    }

    if (res.status === 404) {
      throw new Error(
        `Admin endpoint not found: ${path}. Confirm backend/app/api/routes/admin.py is mounted at /api/v1/admin.`
      );
    }

    if (res.status === 405) {
      throw new Error(
        `Admin endpoint method mismatch for ${method} ${path}. Confirm frontend and backend admin routes are aligned.`
      );
    }

    throw new Error(extractErrorMessage(payload, fallback));
  }

  return payload;
}

function unwrapEnvelope(payload) {
  if (!payload) return {};

  // Backend AdminEnvelope shape: { ok, generated_at, data }
  if (
    Object.prototype.hasOwnProperty.call(payload, "data") &&
    (
      Object.prototype.hasOwnProperty.call(payload, "ok") ||
      Object.prototype.hasOwnProperty.call(payload, "generated_at")
    )
  ) {
    return payload.data ?? {};
  }

  // Axios-like compatibility.
  if (payload.data && typeof payload.data === "object") return payload.data;

  return payload;
}

function normalizeItems(payload) {
  const data = unwrapEnvelope(payload);

  if (Array.isArray(data)) {
    return {
      available: true,
      count: data.length,
      items: data,
      raw: data,
    };
  }

  if (Array.isArray(data.items)) {
    return {
      ...data,
      items: data.items,
      count: Number(data.count ?? data.items.length) || data.items.length,
    };
  }

  if (Array.isArray(data.rows)) {
    return {
      ...data,
      items: data.rows,
      count: Number(data.count ?? data.rows.length) || data.rows.length,
    };
  }

  if (Array.isArray(data.results)) {
    return {
      ...data,
      items: data.results,
      count: Number(data.count ?? data.results.length) || data.results.length,
    };
  }

  return {
    ...data,
    items: [],
    count: Number(data.count || 0) || 0,
  };
}

async function get(path, params = {}) {
  return unwrapEnvelope(await request(`${path}${buildQuery(params)}`));
}

async function getItems(path, params = {}) {
  return normalizeItems(await request(`${path}${buildQuery(params)}`));
}

async function post(path, body = {}) {
  return unwrapEnvelope(
    await request(path, {
      method: "POST",
      body: JSON.stringify(body || {}),
    })
  );
}

function safePathname() {
  try {
    return window.location.pathname || "/";
  } catch {
    return "/";
  }
}

function safeUserAgent() {
  try {
    return navigator.userAgent || "";
  } catch {
    return "";
  }
}

/**
 * Fire-and-forget platform activity logging.
 * This calls POST /api/v1/admin/activity. It should not block user actions.
 */
export async function logActivity({
  event_type,
  message,
  entity_type = null,
  entity_id = null,
  metadata = {},
  level = "info",
  source = "frontend",
} = {}) {
  if (!event_type) return { created: false, reason: "event_type_missing" };

  try {
    return await post("/admin/activity", {
      level,
      source,
      event_type,
      message: message || event_type,
      entity_type,
      entity_id: entity_id == null ? null : String(entity_id),
      metadata: {
        route: safePathname(),
        user_agent: safeUserAgent(),
        timestamp: new Date().toISOString(),
        ...metadata,
      },
    });
  } catch (error) {
    // Never break the user experience because logging failed.
    console.warn("[admin] activity log failed:", error?.message || error);
    return { created: false, error: error?.message || String(error) };
  }
}

/**
 * Log a page view. Use this from App.jsx/Layout route-change effect.
 */
export function trackPageView(pathname = safePathname(), metadata = {}) {
  return logActivity({
    event_type: "page.view",
    message: `Page viewed: ${pathname}`,
    entity_type: "page",
    entity_id: pathname,
    metadata,
  });
}

/**
 * Log admin page views specifically.
 */
export function trackAdminPageView(pathname = safePathname(), metadata = {}) {
  return logActivity({
    event_type: "admin.page.view",
    message: `Admin page viewed: ${pathname}`,
    entity_type: "admin_page",
    entity_id: pathname,
    metadata,
  });
}

export const adminApi = {
  // Low-level helpers
  request,
  unwrapEnvelope,
  normalizeItems,
  logActivity,
  trackPageView,
  trackAdminPageView,

  // Health / bootstrap
  health: () => get("/admin/health"),
  bootstrap: () => post("/admin/bootstrap"),
  overview: () => get("/admin/overview"),

  // Users
  users: ({ limit = 50, offset = 0, search = "" } = {}) =>
    getItems("/admin/users", { limit, offset, search }),

  userDetail: (userId) =>
    get(`/admin/users/${encodeURIComponent(userId)}`),

  suspendUser: (userId, reason = "") =>
    post(`/admin/users/${encodeURIComponent(userId)}/suspend`, { reason }),

  activateUser: (userId, reason = "") =>
    post(`/admin/users/${encodeURIComponent(userId)}/activate`, { reason }),

  updateUserRole: (userId, role, reason = "") =>
    post(`/admin/users/${encodeURIComponent(userId)}/role`, { role, reason }),

  // Organizations
  organizations: ({ limit = 50, offset = 0, search = "" } = {}) =>
    getItems("/admin/organizations", { limit, offset, search }),

  organisations: ({ limit = 50, offset = 0, search = "" } = {}) =>
    getItems("/admin/organisations", { limit, offset, search }),

  // Billing
  paymentsSummary: () => get("/admin/payments/summary"),

  paymentEvents: ({ limit = 50, offset = 0 } = {}) =>
    getItems("/admin/payments/events", { limit, offset }),

  subscriptions: ({ limit = 50, offset = 0 } = {}) =>
    getItems("/admin/subscriptions", { limit, offset }),

  // Activity / audit / observability
  activity: ({ limit = 100, offset = 0 } = {}) =>
    getItems("/admin/activity", { limit, offset }),

  activityLogs: ({ limit = 100, offset = 0 } = {}) =>
    getItems("/admin/activity-logs", { limit, offset }),

  createActivity: (payload) => logActivity(payload),

  systemLogs: ({ limit = 100, offset = 0, level = "" } = {}) =>
    getItems("/admin/system-logs", { limit, offset, level }),

  auditLogs: ({ limit = 100, offset = 0 } = {}) =>
    getItems("/admin/audit-logs", { limit, offset }),

  audit: ({ limit = 100, offset = 0 } = {}) =>
    getItems("/admin/audit", { limit, offset }),

  adminActions: ({ limit = 100, offset = 0 } = {}) =>
    getItems("/admin/admin-actions", { limit, offset }),

  // AI
  aiUsage: ({ limit = 100, offset = 0 } = {}) =>
    getItems("/admin/ai-usage", { limit, offset }),

  // Ingestion
  ingestionJobs: ({ limit = 100, offset = 0 } = {}) =>
    getItems("/admin/ingestion-jobs", { limit, offset }),

  ingestion: ({ limit = 100, offset = 0 } = {}) =>
    getItems("/admin/ingestion-jobs", { limit, offset }),

  // Reports
  reports: ({ limit = 100, offset = 0 } = {}) =>
    getItems("/admin/reports", { limit, offset }),

  // Table inspection
  inspectTable: (tableName, { limit = 50, offset = 0 } = {}) =>
    getItems(`/admin/tables/${encodeURIComponent(tableName)}`, { limit, offset }),
};

export default adminApi;